/**
 * /productos — Lista del catálogo de productos/servicios.
 *
 * Muestra precio, IVA% e INC% por producto (la "calculadora fiscal"
 * usa estos defaults al crear líneas de factura).
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Loader2, Pencil, Plus, Search } from 'lucide-react'

import { AppShell } from '@/components/layout/app-shell'
import { useAuthContext } from '@/hooks/use-auth-context'
import { productApi } from '@/lib/api'
import { ROUTES } from '@/lib/constants'

const COP = new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  maximumFractionDigits: 2,
})

export function ProductsListPage() {
  const { activeOrgId, isLoading: authLoading } = useAuthContext()
  const [search, setSearch] = useState('')
  const [showArchived, setShowArchived] = useState(false)

  const list = useQuery({
    queryKey: ['products', activeOrgId, search, showArchived],
    queryFn: () =>
      productApi.list(activeOrgId!, {
        search: search || undefined,
        only_active: !showArchived,
        limit: 100,
      }),
    enabled: !!activeOrgId,
  })

  if (authLoading) {
    return (
      <AppShell>
        <div className="flex items-center gap-2 p-6 text-sm text-fg-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando contexto...
        </div>
      </AppShell>
    )
  }

  if (!activeOrgId) {
    return (
      <AppShell>
        <div className="rounded-xl border border-error/30 bg-error-tint p-6 text-sm text-error-fg">
          Sin organización activa. <a href="/login" className="underline">Iniciar sesión</a>
        </div>
      </AppShell>
    )
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-5xl">
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Productos</h1>
            <p className="mt-1 text-sm text-fg-secondary">
              Catálogo de productos/servicios con IVA + INC por defecto. Editable per
              línea al facturar.
            </p>
          </div>
          <Link
            to={ROUTES.PRODUCT_NEW}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
          >
            <Plus className="h-4 w-4" /> Nuevo producto
          </Link>
        </header>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre..."
              className="w-full rounded-md border border-border bg-bg-app py-2 pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-fg-secondary">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            Incluir archivados
          </label>
        </div>

        <div className="mt-4 overflow-hidden rounded-xl border border-border bg-bg-surface">
          {list.isLoading && (
            <div className="flex items-center gap-2 p-6 text-sm text-fg-muted">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando...
            </div>
          )}
          {list.isError && (
            <div className="p-6 text-sm text-error-fg">
              Error: {(list.error as Error).message}
            </div>
          )}
          {list.data && list.data.items.length === 0 && (
            <div className="p-6 text-center text-sm text-fg-muted">
              {search ? 'Sin resultados.' : 'Sin productos aún.'}
            </div>
          )}
          {list.data && list.data.items.length > 0 && (
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-bg-elevated text-left text-xs uppercase tracking-wide text-fg-muted">
                <tr>
                  <th className="px-4 py-2.5">Nombre</th>
                  <th className="px-4 py-2.5">SKU</th>
                  <th className="px-4 py-2.5 text-right">Precio</th>
                  <th className="px-4 py-2.5 text-center">IVA</th>
                  <th className="px-4 py-2.5 text-center">INC</th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {list.data.items.map((p) => (
                  <tr key={p.id} className={p.is_active ? 'hover:bg-bg-elevated' : 'text-fg-muted'}>
                    <td className="px-4 py-2.5 font-medium">{p.name}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">{p.sku ?? '—'}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs">
                      {COP.format(parseFloat(p.unit_price))}
                    </td>
                    <td className="px-4 py-2.5 text-center font-mono text-xs">
                      {p.tax_iva_excluded ? 'Excluido' : `${p.tax_iva_percent}%`}
                    </td>
                    <td className="px-4 py-2.5 text-center font-mono text-xs">
                      {parseFloat(p.tax_inc_percent) > 0 ? `${p.tax_inc_percent}%` : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <Link
                        to={ROUTES.PRODUCT_EDIT.replace(':id', p.id)}
                        className="inline-flex items-center gap-1 text-xs text-brand-600 hover:underline"
                      >
                        <Pencil className="h-3.5 w-3.5" /> Editar
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {list.data && (
          <p className="mt-3 text-xs text-fg-muted">
            Mostrando {list.data.items.length} de {list.data.total} productos.
          </p>
        )}
      </div>
    </AppShell>
  )
}
